import { useState } from 'react';
import { Alert, Badge, Button, Card, ListGroup, Modal, Stack } from 'react-bootstrap';
import { FaStar, FaTrash } from 'react-icons/fa';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL, CUSTOMER_API } from '../config';

function CustomerTenantList({
    customerId,
    tenants = [],
    onUpdate,
    readOnly = false
}) {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [tenantToRemove, setTenantToRemove] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedTenants, setSelectedTenants] = useState([]);
    const [primaryTenantId, setPrimaryTenantId] = useState(null);
    // Get existing tenant IDs to exclude from add list - handle both id and tenant_id
    const existingTenantIds = tenants.map(t => t.id || t.tenant_id);

    const handleAddTenants = async () => {
        if (selectedTenants.length === 0) {
            alert('Please select at least one tenant');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(CUSTOMER_API.ASSIGN_TENANTS(customerId), {
                method: 'POST',
                body: JSON.stringify({
                    tenant_ids: selectedTenants,
                    is_primary: primaryTenantId !== null
                })
            });

            if (response.ok) {
                const result = await response.json();
                setShowAddModal(false);
                setSelectedTenants([]);
                setPrimaryTenantId(null);
                onUpdate && onUpdate(result.tenants || []);
                alert('Tenants assigned successfully');
            } else {
                const errorText = await response.text();
                setError(`Failed to assign tenants: ${errorText}`);
            }
        } catch (error) {
            console.error('Error assigning tenants:', error);
            setError('Error assigning tenants');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveTenantClick = (tenantId) => {
        setTenantToRemove(tenantId);
        setShowRemoveModal(true);
    };

    const handleRemoveTenant = async () => {
        if (!tenantToRemove) return;

        setShowRemoveModal(false);
        setLoading(true);
        setError(null);

        try {
            // Use the customer_tenant id (primary key), not tenant_id
            const customerTenantId = tenantToRemove;
            const response = await fetchWithAuth(`${API_BASE_URL}/customers/${customerId}/tenants/${customerTenantId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                const result = await response.json();
                // Update local list immediately for better UX
                const updatedTenants = tenants.filter(t => t.tenant_id !== tenantToRemove);
                onUpdate && onUpdate(updatedTenants);
                alert('Tenant removed successfully');
            } else {
                const errorText = await response.text();
                setError(`Failed to remove tenant: ${errorText}`);
            }
        } catch (error) {
            console.error('Error removing tenant:', error);
            setError('Error removing tenant');
        } finally {
            setLoading(false);
            setTenantToRemove(null);
        }
    };

    const handleSetPrimary = async (tenantId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(CUSTOMER_API.SET_PRIMARY_TENANT(customerId), {
                method: 'PUT',
                body: JSON.stringify({ tenant_id: tenantId })
            });

            if (response.ok) {
                const result = await response.json();
                onUpdate && onUpdate(result.tenants || []);
                alert('Primary tenant updated successfully');
            } else {
                const errorText = await response.text();
                setError(`Failed to set primary tenant: ${errorText}`);
            }
        } catch (error) {
            console.error('Error setting primary tenant:', error);
            setError('Error setting primary tenant');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVE':
                return <Badge variant="success">Active</Badge>;
            case 'INACTIVE':
                return <Badge variant="warning">Inactive</Badge>;
            case 'ENDED':
                return <Badge variant="secondary">Ended</Badge>;
            default:
                return <Badge variant="secondary">{status || 'Unknown'}</Badge>;
        }
    };

    return (
        <div className="customer-tenant-list">
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span><strong>Tenant Memberships</strong></span>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" className="mb-3">
                            {error}
                        </Alert>
                    )}

                    {tenants.length === 0 ? (
                        <div className="text-muted text-center py-3">
                            No tenant memberships found
                        </div>
                    ) : (
                        <ListGroup>
                            {tenants.map(tenant => {
                                // Handle both id (new) and tenant_id (legacy) for backward compatibility
                                const tenantId = tenant.id || tenant.tenant_id;
                                return (
                                    <ListGroup.Item
                                        key={tenantId}
                                        className="d-flex justify-content-between align-items-center"
                                    >
                                        <div style={{ marginLeft: "10px" }} className="d-flex align-items-center justify-content-center">
                                            <strong>{tenant.tenant_name}</strong>
                                            {tenant.is_primary && (
                                                <Badge style={{ marginLeft: "1rem" }} variant="primary" className="ml-2">
                                                    <Stack direction="horizontal" gap={1} alignItems="center" justifyContent="center">
                                                        <FaStar className="mr-1" /> Primary
                                                    </Stack>
                                                </Badge>
                                            )}
                                        </div>
                                        {!readOnly && (
                                            <div className="d-flex gap-2">
                                                {!tenant.is_primary && (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleSetPrimary(tenantId)}
                                                        disabled={loading}
                                                        title="Set as primary tenant"
                                                    >
                                                        <FaStar />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleRemoveTenantClick(tenantId)}
                                                    disabled={loading}
                                                    title="Remove tenant"
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </div>
                                        )}
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>

            {/* Remove Tenant Confirmation Modal */}
            <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Remove Tenant</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to remove this tenant? This will also remove all site associations for this tenant.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleRemoveTenant} disabled={loading}>
                        Remove Tenant
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default CustomerTenantList;